import React, { FC, useContext, useState, useRef, useMemo } from 'react'

import { useHistory, useParams } from 'react-router'
import { useLocalStorage } from 'react-use'
import { dataIdFromObject } from '@sketch/graphql-cache'

import { localStorageKeys } from '@sketch/constants'
import { routes, RouteParams } from '@sketch/modules-common'

import { ReactComponent as PlusIcon } from '@sketch/icons/plus-16'

import {
  Tooltip,
  handleFetchMore,
  ModalContext,
  SelectDropdownButtonItem,
} from '@sketch/components'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ToastContext } from '@sketch/toasts'

import ProjectsList from '../ProjectsList'
import AddNewProjectModal from '../../modals/AddNewProjectModal'
import { ProjectListItem } from '../ProjectListItem'

import {
  MenuWrapper,
  ProjectsHeader,
  WarningIcon,
  Error,
  ErrorText,
  RefetchButton,
  StyledSkeletonSidebarList,
  StyledCollapsibleContent,
  StyledTrigger,
} from './ProjectsMenu.styles'
import { CollapsibleIcon } from '../../../workspace/components/Sidebar/Sidebar.styles'

import {
  useGetProjectsQuery,
  GetProjectsQueryResult,
  ProjectInSidebarAndHeaderFragment,
  WorkspaceMinimalFragment,
  CreateProjectMutation,
  GetProjectsQuery,
} from '@sketch/gql-types'
import { ProjectLink } from '../../types'

export interface ProjectsMenuProps {
  canUserAdministerShares: boolean
  showWorkspaceSettings?: boolean
  workspace: WorkspaceMinimalFragment
  onLoadCompleted: (data: GetProjectsQuery) => void
}

interface ProjectsListContainerProps {
  workspace: WorkspaceMinimalFragment
  projectsResponse: GetProjectsQueryResult
  isUserEditor: boolean
  isUserAdmin: boolean
  isProjectsListDisabled?: boolean
  isCollapsed?: boolean
  showCreateProjectEditor: boolean
  onBlur: () => void
  onAdd: () => void
}

const useProjectNormalizer = (
  entries: ProjectInSidebarAndHeaderFragment[],
  workspaceId: string
): ProjectLink[] => {
  return useMemo(
    () =>
      entries
        .sort((a, b) => {
          // If a is pinned and b is not, sort a to the start
          if (a.pinnedByCurrentUserAt && !b.pinnedByCurrentUserAt) {
            return -1
          }

          // If b is pinned and a is not, sort b to the start
          if (b.pinnedByCurrentUserAt && !a.pinnedByCurrentUserAt) {
            return 1
          }

          // If both a and b are pinned or not pinned, sort by name
          return a.name.localeCompare(b.name, undefined, { numeric: true })
        })
        .map(project => ({
          link: routes.WORKSPACE_PROJECT.create({
            projectId: project.identifier,
            workspaceId,
          }),
          project: project,
        })),
    [entries, workspaceId]
  )
}

/**
 * CONSTANTS
 */
const entriesPath = ['workspace', 'projects', 'entries']

/**
 * HELPER COMPONENTS
 */
const ProjectsListContainer: FC<ProjectsListContainerProps> = props => {
  // This is for setting autofocus just after creating a new folder
  const autofocusLastCreated = useRef(false)

  const {
    workspace,
    isUserEditor,
    isUserAdmin,
    onAdd,
    isProjectsListDisabled,
    isCollapsed,
  } = props

  const workspaceId = workspace.identifier

  const { data, error, loading, fetchMore, refetch } = useGetProjectsQuery({
    variables: { workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.workspaceId !== curr?.workspaceId,
  })

  // TODO: Remove this when migration to identifiers in project URLS
  // is completed. https://github.com/sketch-hq/Cloud/issues/12437
  const { projectId } = useParams<RouteParams<'WORKSPACE_PROJECT'>>()

  const projects = data?.workspace.projects.entries ?? []
  const projectsList = useProjectNormalizer(projects, workspaceId)

  const handleRefetch = () => {
    refetch()
  }

  // We will hide this when it's collapsed with the new Sidebar
  if (loading && !isCollapsed) {
    return <StyledSkeletonSidebarList data-testid="spinner" numberOfItems={4} />
  }

  if (error) {
    return (
      <Error>
        <WarningIcon width="24px" height="24px" />
        <ErrorText>Unable to load projects</ErrorText>
        <RefetchButton type="button" onClick={handleRefetch}>
          Try Again
        </RefetchButton>
      </Error>
    )
  }

  if (!data) {
    return null
  }

  const {
    meta: { after, totalCount },
  } = data.workspace.projects

  if (totalCount === 0) {
    if (isUserEditor || isUserAdmin) {
      if (isCollapsed) {
        return null
      }

      return (
        <SelectDropdownButtonItem
          text="Create Project..."
          icon={PlusIcon}
          onClick={onAdd}
        />
      )
    }

    return (
      <Tooltip
        placement="bottom"
        content="Only Editors and Admins can create new projects"
      >
        <SelectDropdownButtonItem
          text="Create Project..."
          icon={PlusIcon}
          onClick={onAdd}
          disabled
        />
      </Tooltip>
    )
  }

  const handleLoadMore = handleFetchMore(fetchMore, entriesPath, {
    dataIdFromObject,
    after,
  })

  const selectedProject = projectsList.find(
    ({ project }) => projectId === project.identifier
  )

  if (isCollapsed && selectedProject) {
    // If is collapsed we only show the active item, not the list
    const { project, link } = selectedProject

    return (
      <ProjectListItem
        key={project.identifier}
        project={project}
        link={link}
        userCanEdit={isUserEditor}
        userCanAdminister={isUserAdmin}
        workspace={workspace}
        forceHighlight={true}
      />
    )
  }

  return (
    <StyledCollapsibleContent data-testid="projects-content">
      <ProjectsList
        canLoadMore={after !== null}
        onLoadMore={handleLoadMore}
        items={projectsList}
        userCanEdit={isUserEditor}
        userCanAdminister={isUserAdmin}
        workspace={workspace}
        autofocusLastCreated={autofocusLastCreated}
        isDisabled={isProjectsListDisabled}
      />
    </StyledCollapsibleContent>
  )
}

/**
 * MAIN COMPONENTS
 */
export const ProjectsMenu: FC<ProjectsMenuProps> = ({
  canUserAdministerShares,
  workspace,
  onLoadCompleted,
}) => {
  const { showModal, hideModal } = useContext(ModalContext)
  const history = useHistory()
  const workspaceId = workspace.identifier
  const isUserEditor = workspace?.userCanEdit ?? false
  const isProjectsListDisabled = workspace.status === 'INACTIVE'

  const localStorageKey = localStorageKeys.sidebarSectionProjectsCollapsed
  const [
    isCollapsedProjectsSection,
    setIsCollapsedProjectsSection,
  ] = useLocalStorage(localStorageKey)

  const [showCreateProjectEditor, setShowCreateProject] = useState(false)

  const projectsResponse = useGetProjectsQuery({
    variables: { workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.workspaceId !== curr?.workspaceId,
    skip: !workspace,
    onCompleted: onLoadCompleted,
  })

  const { showToast } = useContext(ToastContext)

  const handleOnComplete = (data: CreateProjectMutation) => {
    const { name, identifier: projectId } = data.createWorkspaceProject.project

    showToast(`"${name}" Project created`)

    history.push(
      routes.WORKSPACE_PROJECT.create({
        projectId,
        workspaceId,
      })
    )

    hideModal()
  }

  const handleOnAdd = () => {
    showModal(AddNewProjectModal, {
      workspace,
      onCompleted: handleOnComplete,
    })
    return
  }

  const handleOnBlur = () => {
    setShowCreateProject(false)
  }

  const handleCollapsibleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsCollapsedProjectsSection(!isCollapsedProjectsSection)
  }

  return (
    <MenuWrapper data-testid="projects">
      <Collapsible.Root
        className="CollapsibleRoot"
        open={!isCollapsedProjectsSection}
        onOpenChange={
          setIsCollapsedProjectsSection as (isOpen: boolean) => void
        }
      >
        <ProjectsHeader
          onAdd={handleOnAdd}
          isProjectsListDisabled={isProjectsListDisabled}
          userCanAddProjects={isUserEditor || canUserAdministerShares}
          collapseButton={
            <StyledTrigger data-testid="collapse-projects-button" asChild>
              <CollapsibleIcon
                isOpen={!isCollapsedProjectsSection}
                onClick={handleCollapsibleClick}
              />
            </StyledTrigger>
          }
        />

        {/* We need to include the Collapsible.Content
            one level inside as we need to handle the switch between
            the active project and the list depending if is collapsed or not */}
        <ProjectsListContainer
          workspace={workspace}
          isUserEditor={isUserEditor}
          isUserAdmin={canUserAdministerShares}
          isCollapsed={Boolean(isCollapsedProjectsSection)}
          onBlur={handleOnBlur}
          showCreateProjectEditor={showCreateProjectEditor}
          onAdd={handleOnAdd}
          projectsResponse={projectsResponse}
          isProjectsListDisabled={isProjectsListDisabled}
        />
      </Collapsible.Root>
    </MenuWrapper>
  )
}
